import { useEffect, useState } from "react";
// Session type imported from ./lib/lab-data below
import { resolveTenant, isTenantHomepage } from "./lib/subdomain";
import {
  loadOSBySlug,
  loadOSesByOwner,
  type OperatingSystem,
  type Session,
} from "./lib/lab-data";
import { signIn, signOut } from "./lib/sloeAuthClient";
import { adaptActiveSchema } from "./lib/adapt-schema";
import { setVoiceEnabled } from "./lib/voice-flag";
import { WelcomeScreen } from "./components/welcome/WelcomeScreen";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { CodeOSShell } from "./components/CodeOSShell";
import { resolveTenantBrand } from "./lib/tenant-brand";

const STUDIO_BASE_URL =
  (import.meta as any).env?.VITE_STUDIO_BASE_URL ||
  "https://official-sloe-labs-cdtj.vercel.app";
const STUDIO_BUILD_URL = `${STUDIO_BASE_URL}/build`;

type Status =
  | "loading"
  | "ok_authed"        // viewing slug.sloelabs.com, session present, is owner
  | "ok_locked"        // viewing slug.sloelabs.com, no session or not owner
  | "homepage_authed"  // tenant.sloelabs.com, session present, list Operating Systems (Applications)
  | "homepage_anon"    // tenant.sloelabs.com, no session, prompt sign-in
  | "not_found"
  | "error";

// Sign-in for locked Agentic Operating System subdomains and the tenant home.
// SloeAuth's hosted page (auth.sloelabs.com) does the actual sign-in — Google
// or a one-time code emailed to you — so this card is one branded button that
// sends the browser there and back to this exact page. There is no password
// and no magic link any more; first-time and returning users take the same
// path (the account is created or linked on the way back, by verified email).
// The card keeps its branding (OS name + brand_color accent from the
// public-safe /api/os-by-slug) and the wrong-account affordance.
function SignInCard({ osName, brandColor, signedInAs, footer, authError }: {
  osName: string;
  brandColor?: string;
  // (3c) When the viewer IS authenticated but not the owner of this OS, show a
  // "signed in as X — sign out / switch" affordance so they aren't stuck.
  signedInAs?: { email: string };
  // (3a) Optional footer (e.g. a "build a new OS at Sloe Laboratory" link) for
  // the root-domain sign-in screen.
  footer?: React.ReactNode;
  // Set when SloeAuth's callback bounced back with ?auth_error=<code>.
  authError?: string | null;
}) {
  const accent = brandColor || "#4ade80";
  const monogram = (osName?.trim()?.[0] || "O").toUpperCase();
  const [status, setStatus] = useState<"idle" | "redirecting" | "signing_out" | "signout_failed">("idle");

  const continueToSignIn = () => {
    setStatus("redirecting");
    signIn(window.location.href);
  };
  const switchAccount = async () => {
    setStatus("signing_out");
    const ok = await signOut(window.location.href);
    if (!ok) setStatus("signout_failed");
  };

  return (
    <div style={lockShell}>
      <div style={lockCard}>
        <div style={{ ...monogramBox, background: accent }}>{monogram}</div>
        <h1 style={lockTitle}>{osName}</h1>
        <p style={lockText}>Sign in with Google or a one-time code sent to your email.</p>

        {/* (3c) Wrong-account affordance — authenticated but not the owner */}
        {signedInAs && (
          <div style={signedInBanner}>
            <span style={{ opacity: 0.85 }}>
              Signed in as <strong style={{ color: "#f5f1e8" }}>{signedInAs.email}</strong> — you don't own this Agentic Operating System.
            </span>
            <button type="button" onClick={switchAccount} disabled={status === "signing_out"} style={{ ...lockedSecondaryButton, marginTop: "10px" }}>
              {status === "signing_out" ? "Signing out…" : "Sign out / switch account"}
            </button>
          </div>
        )}

        {!signedInAs && (
          <button
            type="button"
            onClick={continueToSignIn}
            disabled={status === "redirecting"}
            style={{ ...lockedPrimaryButton, width: "100%", background: accent, borderColor: accent, color: "#0a0a0a" }}
          >
            {status === "redirecting" ? "Opening sign-in…" : "Continue"}
          </button>
        )}

        {authError && (
          <p style={lockError}>{authErrorMessage(authError)}</p>
        )}
        {status === "signout_failed" && (
          <p style={lockError}>
            Couldn't sign out — the sign-in service didn't confirm. You're still signed in on this device. Try again.
          </p>
        )}
        {footer && <div style={{ marginTop: "22px" }}>{footer}</div>}
      </div>
    </div>
  );
}

// Human copy for the ?auth_error=<code> SloeAuth's callback can send back.
function authErrorMessage(code: string): string {
  switch (code) {
    case "sign_in_expired":
      return "That sign-in took too long and expired. Continue to try again.";
    case "access_denied":
      return "Sign-in was cancelled. Continue to try again.";
    case "verified_email_required":
      return "We need a verified email address to open your account. Sign in with Google or an email code.";
    case "account_link_failed":
      return "We couldn't open your account just now. Try again in a moment.";
    default:
      return "Sign-in didn't complete. Continue to try again.";
  }
}

// First name for the Operator greeting. Tries user_metadata.first_name,
// then the local-part of the email (chunk before the first . _ or +),
// then gives up so the greeting renders nameless.
function deriveFirstName(user: Session["user"] | undefined): string | undefined {
  if (!user) return undefined;
  const meta = user.user_metadata?.first_name;
  if (typeof meta === "string" && meta.trim().length > 0) return meta.trim();
  const local = user.email?.split("@")[0] ?? "";
  if (!local) return undefined;
  const chunk = local.split(/[._+]/)[0] || local;
  if (!chunk || !/^[A-Za-z]/.test(chunk)) return undefined;
  return chunk.charAt(0).toUpperCase() + chunk.slice(1).toLowerCase();
}

// Sign out from the plain (non-card) screens. signOut() navigates on success;
// on failure the session is still live on this device — say so.
async function signOutOrExplain(): Promise<void> {
  const ok = await signOut(window.location.origin + "/");
  if (!ok) window.alert("Couldn't sign out — the sign-in service didn't confirm. You're still signed in on this device. Try again.");
}

export default function App() {
  const [slug, setSlug] = useState<string | null>(null);
  const [os, setOs] = useState<OperatingSystem | null>(null);
  const [ownedOSes, setOwnedOSes] = useState<OperatingSystem[]>([]);
  const [session, setSession] = useState<Session | null>(null);
  // (3b) Set when the app boots with a pre-SloeAuth magic-link fragment
  // (#access_token=… from an old email): those links no longer sign anyone in,
  // so say so plainly instead of silently showing the lock screen.
  const [linkExpired, setLinkExpired] = useState(false);
  // ?auth_error=<code> from the SloeAuth callback (read once, then stripped).
  const [authError] = useState<string | null>(() => {
    if (typeof window === "undefined") return null;
    const params = new URLSearchParams(window.location.search);
    const code = params.get("auth_error");
    if (!code) return null;
    params.delete("auth_error");
    const qs = params.toString();
    window.history.replaceState(null, "", window.location.pathname + (qs ? `?${qs}` : "") + window.location.hash);
    return code.slice(0, 40);
  });
  const [status, setStatus] = useState<Status>("loading");
  const [error, setError] = useState<string | null>(null);
  const [installPrompt, setInstallPrompt] = useState<any>(null);
  // P-1A — already-installed detection. Seeded synchronously from display-mode /
  // iOS standalone so a session launched from the installed app starts "true";
  // the appinstalled listener and a per-Agentic Operating System localStorage latch (below) cover the
  // browser-tab case where standalone is false but the Agentic Operating System was installed before.
  const [appInstalled, setAppInstalled] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return (
      window.matchMedia?.("(display-mode: standalone)")?.matches === true ||
      (window.navigator as any)?.standalone === true
    );
  });
  // P2.4 — first-arrival welcome. Initial value derived from the URL so a
  // post-claim ?welcome=true survives the magic-link bootstrap, and a refresh
  // during the welcome state keeps showing it (cleared only when the user
  // clicks "Show me my Agentic Operating System").
  const [showWelcome, setShowWelcome] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return new URLSearchParams(window.location.search).get("welcome") === "true";
  });

  // White-label: reflect the loaded OS's brand in the browser tab title and theme-color meta.
  // Neutral fallback ("Your Operating System") until an OS loads — never "Sloe".
  useEffect(() => {
    const brand = resolveTenantBrand({ osName: os?.os_name });
    document.title = brand.name;

    const brandColor = (os?.active_schema as any)?.metadata?.brand_color || "#0A0A0A";
    let meta = document.querySelector('meta[name="theme-color"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "theme-color");
      document.head.appendChild(meta);
    }
    meta.setAttribute("content", brandColor);
    let appleTitle = document.querySelector('meta[name="apple-mobile-web-app-title"]');
    if (!appleTitle) {
      appleTitle = document.createElement("meta");
      appleTitle.setAttribute("name", "apple-mobile-web-app-title");
      document.head.appendChild(appleTitle);
    }
    appleTitle.setAttribute("content", brand.name);

    let appleIcon = document.querySelector('link[rel="apple-touch-icon"]');
    if (!appleIcon) {
      appleIcon = document.createElement("link");
      appleIcon.setAttribute("rel", "apple-touch-icon");
      appleIcon.setAttribute("sizes", "180x180");
      document.head.appendChild(appleIcon);
    }
    const colorHex = brandColor.replace("#", "");
    appleIcon.setAttribute("href", `/api/icon?format=png&size=180&name=${encodeURIComponent(brand.name)}&color=${encodeURIComponent(colorHex)}`);
  }, [os?.os_name, os?.active_schema]);

  useEffect(() => {
    const resolvedSlug = resolveTenant(window.location.hostname);
    const isHomepage = isTenantHomepage(window.location.hostname);

    async function resolveAuthAndOS(sess: Session | null) {
      try {
        if (isHomepage) {
          if (sess?.user) {
            const oses = await loadOSesByOwner(sess.user.id);
            setOwnedOSes(oses);
            if (oses.length === 1 && oses[0].subdomain_slug) {
              window.location.href = `https://${oses[0].subdomain_slug}.sloelabs.com`;
              return;
            }
            setStatus("homepage_authed");
          } else {
            setStatus("homepage_anon");
          }
          return;
        }
        if (!resolvedSlug) { setStatus("not_found"); return; }
        setSlug(resolvedSlug);
        const data = await loadOSBySlug(resolvedSlug);
        if (!data) { setStatus("not_found"); return; }
        setOs(data);
        if (sess?.user && sess.user.id === data.owner_id) {
          setStatus("ok_authed");
        } else {
          setStatus("ok_locked");
        }
      } catch (err: any) {
        setError(err?.message ?? "unknown error");
        setStatus("error");
      }
    }

    async function bootstrap() {
      // Step 1: an old (pre-SloeAuth) magic link lands with #access_token=… —
      // never forward those tokens anywhere; drop the fragment and explain.
      const hash = window.location.hash;
      if (hash.includes("access_token=") || hash.includes("type=recovery")) {
        window.history.replaceState(null, "", window.location.pathname + window.location.search);
        setLinkExpired(true);
      }

      // Step 2: fetch /api/auth/me to read the cookie and get the user.
      try {
        const r = await fetch("/api/auth/me", { credentials: "include" });
        const json = await r.json();
        const user = json?.user as Session["user"] | null;
        // Global voice kill-switch: mirror the server flag into the client
        // singleton (read by the voice entry points to hide them). Boolean only.
        setVoiceEnabled(json?.voice_enabled === true);
        const sess: Session | null = user
          ? {
              user,
              is_founder: json?.is_founder === true,
              voice_enabled: json?.voice_enabled === true,
            }
          : null;
        setSession(sess);
        await resolveAuthAndOS(sess);
      } catch (e: any) {
        setError(e?.message ?? "unknown error");
        setStatus("error");
      }
    }

    bootstrap();

    // No client-side onAuthStateChange — server cookie is source of truth.
    const listener = { subscription: { unsubscribe: () => {} } };

    return () => listener.subscription.unsubscribe();
  }, []);

  useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault();
      setInstallPrompt(e);
    };
    const onInstalled = () => setAppInstalled(true);
    window.addEventListener("beforeinstallprompt", handler);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  // P-1A — persist a per-Agentic Operating System install latch once we know both the Agentic Operating System id and that
  // it's installed, so a later visit in a plain browser tab (standalone=false)
  // still recognizes the install and shows the "Welcome back" variant.
  useEffect(() => {
    if (appInstalled && os?.id) {
      try { localStorage.setItem(`sloe_installed_${os.id}`, "true"); } catch { /* private mode */ }
    }
  }, [appInstalled, os?.id]);

  // True if installed this session (standalone / appinstalled) OR a prior install
  // was latched for the Agentic Operating System on this device.
  const alreadyInstalled =
    appInstalled ||
    (!!os?.id && (() => {
      try { return localStorage.getItem(`sloe_installed_${os.id}`) === "true"; }
      catch { return false; }
    })());

  // J1-CLIENT — keep Sentry user + workspace tag in sync with auth state.
  // Indirected through window.Sentry so this is a no-op when no DSN was
  // configured at init time (dev / preview without observability).
  useEffect(() => {
    const SentryGlobal = (window as any).Sentry;
    if (!SentryGlobal?.setUser) return;
    if (session?.user?.id) {
      SentryGlobal.setUser({ id: session.user.id });
    } else {
      SentryGlobal.setUser(null);
    }
    if (os?.id) {
      SentryGlobal.setTag("workspace_id", os.id);
    } else {
      SentryGlobal.setTag("workspace_id", null);
    }
  }, [session?.user?.id, os?.id]);

  // (3b) An old emailed sign-in link — explain, then continue to SloeAuth.
  if (linkExpired && !session) {
    return (
      <div style={shell}>
        <h1 style={title}>Sign-in has moved</h1>
        <p style={text}>
          Sign-in links from before our update no longer work. Continue to sign in with Google or a one-time code sent to your email.
        </p>
        <p style={{ ...text, marginTop: "20px" }}>
          <button style={button} onClick={() => signIn(window.location.href)}>
            Continue to sign in
          </button>
        </p>
      </div>
    );
  }

  if (status === "loading") {
    return (
      <div style={shell}>
        <h1 style={title}>Loading...</h1>
      </div>
    );
  }

  if (status === "not_found") {
    return (
      <div style={shell}>
        <h1 style={title}>Agentic Operating System not found</h1>
        <p style={text}>
          {slug
            ? `No operating system at ${slug}.sloelabs.com`
            : "This subdomain is not configured."}
        </p>
        <p style={text}>
          <a style={link} href={STUDIO_BUILD_URL}>
            Build your own OS in Sloe Laboratory →
          </a>
        </p>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div style={shell}>
        <h1 style={title}>Something went wrong</h1>
        <p style={text}>{error}</p>
      </div>
    );
  }

  if (status === "homepage_anon") {
    // (3a) Real sign-in affordance on the root domain — a returning user who
    // hits tenant.sloelabs.com without their slug is no longer dead-ended.
    // After sign-in they land back here → single-OS auto-redirect / OS list,
    // so they never need to know the slug.
    return (
      <SignInCard
        osName="Sloe Labs"
        authError={authError}
        footer={
          <p style={{ ...lockText, fontSize: "13px", opacity: 0.6, marginBottom: 0 }}>
            No account yet? Build a new OS at{" "}
            <a style={{ ...link, color: "#f5f1e8" }} href={STUDIO_BUILD_URL}>Sloe Laboratory</a>.
          </p>
        }
      />
    );
  }

  if (status === "homepage_authed") {
    return (
      <div style={shell}>
        <h1 style={title}>Your operating systems</h1>
        {ownedOSes.length === 0 ? (
          <p style={text}>
            You're signed in, but you don't have any workspaces yet.{" "}
            <a style={link} href={STUDIO_BUILD_URL}>
              Build one in Sloe Laboratory →
            </a>
          </p>
        ) : (
          <ul style={list}>
            {ownedOSes.map((o) => (
              <li key={o.id} style={listItem}>
                <a style={link} href={`https://${o.subdomain_slug}.sloelabs.com`}>
                  {o.os_name || "Untitled Agentic Operating System"}
                </a>
                <span style={muted}>
                  {" "}
                  — {o.subdomain_slug}.sloelabs.com
                </span>
              </li>
            ))}
          </ul>
        )}
        <p style={{ ...text, marginTop: "24px" }}>
          <button style={button} onClick={() => { void signOutOrExplain(); }}>
            Sign out
          </button>
        </p>
      </div>
    );
  }

  if (status === "ok_locked") {
    // (3c) ok_locked fires for BOTH "no session" and "signed in as the wrong
    // account (not the owner)". When a session exists, surface a sign-out/switch
    // affordance so a wrong-account user isn't stuck. Sign out revokes at
    // SloeAuth, ends its session too, and comes back here to choose an account.
    return (
      <SignInCard
        osName={os?.os_name ?? "Untitled Agentic Operating System"}
        brandColor={os?.brand_color ?? undefined}
        authError={authError}
        signedInAs={session?.user ? { email: session.user.email } : undefined}
      />
    );
  }

  // status === "ok_authed"
  // P2.4 — intercept on first arrival (?welcome=true after the claim flow).
  // Renders the WelcomeScreen *instead of* SchemaRenderer until the user
  // clicks "Show me my Agentic Operating System"; ?welcome=true is the source of truth so a
  // refresh mid-welcome keeps the user on this screen.
  if (showWelcome && os?.os_name) {
    return (
      <WelcomeScreen
        businessName={os.os_name}
        installPrompt={installPrompt}
        alreadyInstalled={alreadyInstalled}
        onShowOS={() => {
          setShowWelcome(false);
          const url = new URL(window.location.href);
          url.searchParams.delete("welcome");
          window.history.replaceState(null, "", url.pathname + url.search);
        }}
        onInstall={async () => {
          if (!installPrompt) return;
          installPrompt.prompt();
          await installPrompt.userChoice;
          setInstallPrompt(null);
        }}
      />
    );
  }

  const adapted = os ? adaptActiveSchema(os.active_schema) : null;

  // Code-bundle Agentic Operating System: mount the generated app (served under /_generated/<os_id>/
  // by the tenant server) inside CodeOSShell — the Sloe chrome (Workspace +
  // compulsory Systems nav) wrapped around the same-origin iframe. The iframe's
  // fetch('/api/...', {credentials:'include'}) carries the .sloelabs.com cookie;
  // os_id is baked into the served index.html. Checked before `adapted` because
  // adaptActiveSchema returns null for a code manifest.
  if (os && (os.active_schema as any)?.bundle_type === "code") {
    return (
      <ErrorBoundary scope="your Agentic Operating System">
        <CodeOSShell os={os} userFirstName={deriveFirstName(session?.user)} showCommand={os?.show_command === true} user={session?.user ?? null} />
      </ErrorBoundary>
    );
  }

  if (adapted) {
    // X2 — catastrophic catch-all. Per-screen boundaries inside
    // the embedded schema screens handle local crashes; this one is the final
    // net so a render error in the Agentic shell itself doesn't blank the
    // whole page.
    return (
      <ErrorBoundary scope="your Agentic Operating System">
        <CodeOSShell os={os!} userFirstName={deriveFirstName(session?.user)} schema={adapted} showCommand={os?.show_command === true} user={session?.user ?? null} />
      </ErrorBoundary>
    );
  }
  return (
    <div style={shell}>
      <h1 style={title}>{os?.os_name ?? "Untitled Agentic Operating System"}</h1>
      <p style={text}>Tenant slug: {slug}</p>
      <p style={text}>Industry: {os?.industry_shape ?? "—"}</p>
      <p style={text}>Signed in as: {session?.user.email}</p>
      <p style={text}>
        Your Agentic Operating System isn’t ready yet — schema generation is pending. Come back soon.
      </p>
      <p style={{ ...text, marginTop: "24px", display: "flex", gap: "12px" }}>
        {installPrompt && (
          <button
            style={button}
            onClick={async () => {
              installPrompt.prompt();
              await installPrompt.userChoice;
              setInstallPrompt(null);
            }}
          >
            Install Agentic Operating System
          </button>
        )}
        <button style={button} onClick={() => { void signOutOrExplain(); }}>
          Sign out
        </button>
      </p>
    </div>
  );
}

const shell: React.CSSProperties = {
  minHeight: "100vh",
  background: "#0a0a0a",
  color: "#f5f1e8",
  fontFamily: "Inter, system-ui, sans-serif",
  padding: "48px 32px",
  maxWidth: "720px",
  margin: "0 auto",
};
const title: React.CSSProperties = { fontSize: "32px", marginBottom: "16px" };
const text: React.CSSProperties = { fontSize: "16px", lineHeight: 1.6, opacity: 0.8 };
const link: React.CSSProperties = { color: "#4ade80", textDecoration: "underline" };
const list: React.CSSProperties = { listStyle: "none", padding: 0, marginTop: "24px" };
const listItem: React.CSSProperties = { padding: "12px 0", borderBottom: "1px solid #1f1f1f" };
const muted: React.CSSProperties = { opacity: 0.5, fontSize: "14px" };
const button: React.CSSProperties = {
  background: "transparent",
  color: "#f5f1e8",
  border: "1px solid #2a2a2a",
  padding: "8px 16px",
  fontSize: "14px",
  cursor: "pointer",
  fontFamily: "inherit",
};
const lockedPrimaryButton: React.CSSProperties = {
  background: "#f5f1e8",
  color: "#0a0a0a",
  border: "1px solid #f5f1e8",
  borderRadius: "8px",
  padding: "12px 16px",
  fontSize: "15px",
  fontWeight: 600,
  cursor: "pointer",
  fontFamily: "inherit",
};

// TENANT-3 — branded lock-screen styles. Centered card on the dark shell, accent
// (brand_color) applied to the monogram + primary button. Kept separate from the
// shared shell/title/text (used by App's other status branches).
const lockShell: React.CSSProperties = {
  minHeight: "100vh",
  background: "#0a0a0a",
  color: "#f5f1e8",
  fontFamily: "Inter, system-ui, sans-serif",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "24px",
};
const lockCard: React.CSSProperties = {
  width: "100%",
  maxWidth: "380px",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  textAlign: "center",
  background: "#101010",
  border: "1px solid #1f1f1f",
  borderRadius: "16px",
  padding: "40px 32px",
};
const monogramBox: React.CSSProperties = {
  width: "48px",
  height: "48px",
  borderRadius: "12px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "#0a0a0a",
  fontSize: "22px",
  fontWeight: 700,
  marginBottom: "20px",
};
const lockTitle: React.CSSProperties = { fontSize: "24px", fontWeight: 600, margin: "0 0 8px" };
const lockText: React.CSSProperties = { fontSize: "15px", lineHeight: 1.6, opacity: 0.75, margin: "0 0 20px" };
const signedInBanner: React.CSSProperties = {
  width: "100%",
  fontSize: "13px",
  lineHeight: 1.5,
  color: "#e6e1d6",
  background: "rgba(255,255,255,0.05)",
  border: "1px solid rgba(255,255,255,0.12)",
  borderRadius: "12px",
  padding: "14px 16px",
  margin: "0 0 20px",
  textAlign: "left",
  display: "flex",
  flexDirection: "column",
};
const lockError: React.CSSProperties = { fontSize: "13px", color: "#e08a7a", marginTop: "14px" };
const lockedSecondaryButton: React.CSSProperties = {
  background: "transparent",
  color: "#f5f1e8",
  border: "1px solid #2a2a2a",
  borderRadius: "8px",
  padding: "12px 16px",
  fontSize: "15px",
  fontWeight: 600,
  cursor: "pointer",
  fontFamily: "inherit",
};
